import React, { useMemo, useState } from 'react';
import InsightCard from './InsightCard';
import useObservations from './useObservations';
import ImportingNotice from './ImportingNotice';
import { FdaDisclaimer } from '../supplements/Disclosures';

// The Insights feed (2026-10 restyle): every open observation, biggest first,
// behind one row of filter pills; the quiet line for what did not move; your
// record (answered or set aside) folded underneath.

const FILTERS = [
  { key: null, label: 'All' },
  { key: 'today', label: 'Today', dot: 'bg-off-strong dark:bg-off' },
  { key: 'shift', label: 'Changed', dot: 'bg-fg-3' },
  { key: 'tendency', label: 'Patterns', dot: 'bg-fg-3' },
  { key: 'stack', label: 'Since you started', dot: 'bg-good-strong dark:bg-good' },
];

const ObservationFeed = ({ onAsk }) => {
  const { data, loading, failed, resolve } = useObservations({ history: true });
  const [filter, setFilter] = useState(null);
  const [whyId, setWhyId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const items = useMemo(() => {
    const all = data?.items || [];
    return filter ? all.filter((i) => i.kind === filter) : all;
  }, [data, filter]);
  const history = useMemo(() => {
    const all = data?.history || [];
    return filter ? all.filter((i) => i.kind === filter) : all;
  }, [data, filter]);
  const counts = data?.counts || {};
  const total = (data?.items || []).length;
  const toggleWhy = (item) => setWhyId((cur) => (cur === item.id ? null : item.id));

  return (
    <div>
      <div className="mb-3.5 mt-[18px] flex flex-wrap gap-2" role="tablist" aria-label="Kinds of observation">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          const n = f.key ? counts[f.key] || 0 : total;
          return (
            <button key={f.label} type="button" role="tab" aria-selected={active} onClick={() => setFilter(f.key)}
              className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${
                active ? 'border-fg bg-fg text-canvas' : 'border-line-strong text-fg-2 hover:text-fg'}`}>
              {f.dot && <i className={`inline-block h-2 w-2 rounded-full ${active ? 'bg-canvas' : f.dot}`} />}
              {f.label}
              <span className="tabular-nums opacity-60">{n}</span>
            </button>
          );
        })}
      </div>

      {loading && <p className="text-fg-3">Looking at your data…</p>}
      {failed && <p className="text-fg-3">Couldn’t load your observations just now.</p>}

      {!loading && !failed && items.length === 0 && !filter && data?.quiet?.reason === 'importing' && (
        <ImportingNotice quiet={data.quiet} />
      )}
      {!loading && !failed && items.length === 0 && !(!filter && data?.quiet?.reason === 'importing') && (
        <div className="flex items-center gap-3.5 rounded-xl border border-line px-5 py-4 text-fg-2">
          <i className="h-2.5 w-2.5 flex-none rounded-full bg-good-strong dark:bg-good" />
          <div>
            <b className="font-medium text-fg">{filter ? `Nothing ${FILTERS.find((f) => f.key === filter)?.label.toLowerCase()} right now.` : 'Nothing unusual.'}</b>{' '}
            {filter ? 'When one shows up it lands here, biggest first.' : (data?.quiet?.text || 'We’ll tell you here when something in your data moves.')}
            {!filter && data?.quiet?.last_data_day && data.quiet.reason === 'stale' && <span className="block text-sm text-fg-3">Latest data: {data.quiet.last_data_day}</span>}
          </div>
        </div>
      )}

      <div className="grid gap-3.5">
        {items.map((item) => (
          <InsightCard key={item.id} item={item} open={whyId === item.id} onToggleWhy={toggleWhy}
            onResolved={(id) => { setWhyId(null); resolve(id); }} onAsk={onAsk} />
        ))}
        {items.some((i) => i.suggestion) && <FdaDisclaimer />}
      </div>

      {!loading && items.length > 0 && data?.quiet?.text && (
        <div className="mt-10 flex items-center gap-3.5 rounded-xl border border-line px-5 py-4 text-fg-2">
          <i className="h-2.5 w-2.5 flex-none rounded-full bg-good-strong dark:bg-good" />
          <div>{data.quiet.text}</div>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-10">
          <button type="button" onClick={() => setShowHistory((s) => !s)} aria-expanded={showHistory}
            className="flex w-full items-center justify-between rounded-xl border border-line bg-transparent px-5 py-4 text-left hover:bg-raised">
            <span className="text-base font-medium">Your record <span className="ml-2 text-sm font-normal text-fg-3">{history.length} answered or set aside</span></span>
            <span className="text-fg-3" aria-hidden="true">{showHistory ? '▴' : '▾'}</span>
          </button>
          {showHistory && (
            <div className="mt-3 grid gap-3">
              {history.map((item) => <InsightCard key={item.id} item={item} compact />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ObservationFeed;
